import React, { useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import "../theme/bdrv2-tokens.css";
import "../theme/bdrv2-base.css";
import "./publicV2Layout.css";

import NavbarV2 from "./NavbarV2";
import FooterV2 from "./FooterV2";
import ThemeToggleV2 from "../theme/ThemeToggleV2";


const readPref = (key, fallback) => {
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};
const writePref = (key, val) => {
  try {
    window.localStorage.setItem(key, val);
  } catch {
    /* ignore */
  }
};

/**
 * Shared shell for every v2 page in BOTH bases:
 * - PUBLIC (preview=false, default): this IS the live site. Theme locked to
 *   dark, indexable (no robots meta), no theme toggle / exit-preview affordance,
 *   nav/footer link to canonical public paths.
 * - PREVIEW (preview=true, /dev-preview/*): developer-only. Theme toggle + bg
 *   toggle + exit link, noindex, nav/footer link to /dev-preview/* paths.
 *
 * Chrome suppression runs in useLayoutEffect (BEFORE paint) so the global V1
 * nav/footer/sticky-tab never flash on a cold load, and restores on unmount.
 * Owns its own fixed background layer; does not rely on the global .bg-img.
 *
 * `displayNav`, `changeStickyTab`, `changeFooter` are the App-level setters,
 * passed from the route element (same wiring as CustomerQuotePage).
 */
const PublicV2Layout = ({
  seo = {},
  children,
  displayNav,
  changeStickyTab,
  changeFooter,
  hideChrome = false,
  preview = false,
}) => {
  const [theme, setTheme] = useState(() =>
    preview ? readPref("bdrv2_theme", "dark") : "dark"
  );
  /*
   * Locked to dark on the public site regardless of any persisted preview pref.
   *
   * There used to be a `bgMode` beside this that read the SAME kind of pref
   * with NO `preview` guard, and applied it unconditionally as `data-bg` — so a
   * developer who clicked "Gradient BG" once in /dev-preview saw the LIVE
   * public site differently from every real visitor, permanently, from
   * localStorage. If another appearance pref is ever added here, guard it on
   * `preview` the way this line does.
   */
  const activeTheme = preview ? theme : "dark";

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    displayNav?.(false);
    changeStickyTab?.(false);
    changeFooter?.(false);
    document.body.classList.add("bdrv2-active");
    return () => {
      displayNav?.(true);
      changeStickyTab?.(true);
      changeFooter?.(true);
      document.body.classList.remove("bdrv2-active");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onThemeToggle = () =>
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      writePref("bdrv2_theme", next);
      return next;
    });

  return (
    <div
      className="bdrv2-root"
      data-theme={activeTheme}
    >
      <Helmet>
        {seo.title ? <title>{seo.title}</title> : null}
        {seo.description ? (
          <meta name="description" content={seo.description} />
        ) : null}
        {/* noindex ONLY in the developer preview; the public site must index */}
        {preview ? <meta name="robots" content="noindex, nofollow" /> : null}
      </Helmet>

      {/*
        The app surface. It no longer carries the wood photo: that image
        belongs to the Home page's About banner, which owns its OWN copy
        (homeV2.css — a literal `url()`, not this token) and is untouched by
        anything here. Handing the same photo to this full-page layer is what
        put it behind every public page.
      */}
      <div className="bdrv2-bg" aria-hidden="true" />

      <div className="bdrv2-content">
        {hideChrome ? null : <NavbarV2 preview={preview} />}
        <main className="bdrv2-main">{children}</main>
        {hideChrome ? null : <FooterV2 preview={preview} />}
      </div>

      {/* theme/bg toggle + exit-to-admin are developer-preview affordances only */}
      {preview ? (
        <>
          <ThemeToggleV2
            theme={theme}
            onThemeToggle={onThemeToggle}
          />
          <Link to="/bdr-office/tasks" className="bdrv2-exit" title="Exit preview to admin">
            &larr; Exit preview
          </Link>
        </>
      ) : null}
    </div>
  );
};

export default PublicV2Layout;
